import React from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";

const SmallMultiple = ({ data, metric, color, title, smoothLine, minYAxis, maxYAxis }) => {
  // Helper to format numbers to 4 decimals.
  const formatValue = (value) => {
    return typeof value === "number" ? Number(value.toFixed(4)) : value;
  };

  // Get the latest data point for the reference line.
  const latestData = data.length > 0 ? data[data.length - 1] : null;
  const latestValue = latestData ? Number(latestData[metric]) : 0;

  // Create a gradient ID based on the metric name.
  const gradientId = `color-${metric.replace(/\s+/g, "_")}`;

  // Custom tooltip component.
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const value = formatValue(payload[0].value);
      return (
        <div
          style={{
            backgroundColor: "white",
            padding: "0.75rem",
            border: "1px solid #e5e7eb",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            borderRadius: "4px",
          }}
        >
          <p style={{ margin: 0, fontWeight: 500, color: "#374151" }}>
            {payload[0].payload.date}
          </p>
          <p style={{ margin: 0, fontSize: "0.875rem", color: "#4B5563" }}>
            {`${title}: ${value}`}
          </p>
        </div>
      );
    }
    return null;
  };

  // Function to generate a lighter color for the fill.
  const getLighterColor = (hexColor, opacity) => {
    if (hexColor.startsWith("#")) {
      const r = parseInt(hexColor.slice(1, 3), 16);
      const g = parseInt(hexColor.slice(3, 5), 16);
      const b = parseInt(hexColor.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }
    return hexColor;
  };

  const fillColor = getLighterColor(color, 0.2);

  // Format the X-axis value.
  const formatXAxis = (value) => {
    const parsedDate = new Date(value);
    return !isNaN(parsedDate.getTime()) ? parsedDate.getFullYear() : value;
  };

  // Set YAxis domain.
  const yDomain = [minYAxis !== undefined ? minYAxis : 0, maxYAxis !== undefined ? maxYAxis : "auto"];

  return (
    <div style={{ width: "100%", height: "100%" }}>
      {/* Centered chart title */}
      <div style={{ textAlign: "center", width: "100%", marginBottom: "0.25rem" }}>
        <strong>{title}</strong>
      </div>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 30 }}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={color} stopOpacity={0.4} />
              <stop offset="95%" stopColor={color} stopOpacity={0.1} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" opacity={0.1} stroke="#e5e7eb" />
          <XAxis
            dataKey="date"
            tickFormatter={formatXAxis}
            tick={{ fontSize: 10, fill: "#6b7280" }}
            tickMargin={10}
            tickLine={false}
            axisLine={{ stroke: "#f3f4f6" }}
          />
          <YAxis
            tick={{ fontSize: 10, fill: "#6b7280" }}
            tickLine={false}
            axisLine={false}
            domain={yDomain}
            tickFormatter={(value) => formatValue(value)}
          />
          <Tooltip content={<CustomTooltip />} />
          {data.some((d) => d[metric] !== undefined) && (
            <Area
              type={smoothLine ? "monotone" : "linear"}
              dataKey={metric}
              stroke={color}
              strokeWidth={2}
              fillOpacity={1}
              fill={`url(#${gradientId})`}
            />
          )}
          {latestData && (
            <ReferenceLine
              y={latestValue}
              stroke={color}
              strokeDasharray="3 3"
              strokeOpacity={0.7}
              label={{
                value: `${formatValue(latestValue)}`,
                fill: color,
                fontSize: 10,
                position: "right",
              }}
            />
          )}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default SmallMultiple;
